import { SchemeTypes } from 'config';
import _ from 'lodash';
// import { useSelector } from 'react-redux';
import useShallowEqualSelector from '../../../custom_hooks/useShallowEqualsSelector';
import { ReducerKeys } from '../../../redux/reducers';
import { StateStatus } from '../../utils/common';

interface IIdentificationDetails {
  vat_id_num: string;
  company_name: string;
  vat_return_id: number;
  vat_return_version_id: number;
  vat_return_reference: number;
}

interface IVatReturnPeriodDetails {
  vat_return_period_id: number;
  vat_return_period: string;
  vat_return_submission_date: Date;
  vat_return_version: Date;
  year: number;
  start_date?: Date;
  end_date?: Date;
}

interface IQuarters {
  quarter_id: number;
  starting_month: number;
  ending_month: number;
  short_text: string;
}

interface IVatReturnDetail {
  vat_return_status_id: number;
  vat_return_state: string;
  identification_details: IIdentificationDetails;
  vat_return_period_details: IVatReturnPeriodDetails;
  oss_scheme: SchemeTypes;
  period: {
    year: number;
    quarter: IQuarters;
  };
  nonUnionSchemeDetail: INonUnionSchemeDetail;
}

interface ISuppliesFromMemberStateOfIdentification {
  vat_return_supply_id: number;
  supply_type: string;
  supply_type_id: number;
  consumer_id: number;
  consumer: string;
  vat_rate_id: number;
  vat_rate: number;
  vat_rate_type: string;
  taxable_amount: number;
  vat_amount: number;
}

interface ISuppliesFromMemberStateOfIdentificationOutput {
  items: Array<ISuppliesFromMemberStateOfIdentification>;
  total_vat_amount_payable_services: number;
  total_vat_amount_payable_goods: number;
}

interface ISuppliesFromFixedEstablishments {
  vat_return_supply_id: number;
  supply_type: string;
  supply_type_id: number;
  consumer_id: number;
  consumer: string;
  vat_rate_id: number;
  vat_rate: number;
  vat_rate_type: string;
  taxable_amount: number;
  vat_amount: number;
  fixed_establishment: number;
}

interface IMscon {
  vat_return_version_id: number;
  country_id: number;
  country: string;
  grand_total: number;
  total_msid_goods: number;
  total_msid_services: number;
  total_msest_goods: number;
  total_msest_services: number;
  due_balance: number;
  total_correction: number;
  total_amount_due: number;
}

interface ISuppliesFromFixedEstablishmentOutput {
  items: Array<ISuppliesFromFixedEstablishments>;
  total_vat_amount_payable_services: number;
  total_vat_amount_payable_goods: number;
}

interface IOutputVatReturnDetail {
  data: IVatReturnDetail;
  status: StateStatus;
  suppliesFromMemberStateOfIdentification: ISuppliesFromMemberStateOfIdentificationOutput;
  suppliesFromFixedEstablishments: ISuppliesFromFixedEstablishmentOutput;
  mscon: Array<IMscon>;
}

interface INonUnionSchemeDetail {
  notEstablished?: boolean;
  changeDate?: string;
  registrationId?: string;
  applicantId?: string;
}

export const useGetVatReturnDetailPeriod =
  (): IOutputVatReturnDetail['data']['period'] => {
    const data = useShallowEqualSelector((state: any) =>
      _.get(
        state[ReducerKeys.VATRETURN_DETAIL_REDUCER],
        'vatReturnDetail.data.period'
      )
    );
    return data;
  };

export const useGetVatReturnDetail = (): IOutputVatReturnDetail => {
  const data = useShallowEqualSelector(
    (state: any) => state[ReducerKeys.VATRETURN_DETAIL_REDUCER].vatReturnDetail
  );
  return data;
};

export const useGetSuppliesFromMemberStateOfIdentification =
  (): ISuppliesFromMemberStateOfIdentificationOutput => {
    const data = useShallowEqualSelector((state: any) =>
      _.get(
        state[ReducerKeys.VATRETURN_DETAIL_REDUCER],
        'vatReturnDetail.suppliesFromMemberStateOfIdentification'
      )
    );
    return data;
  };

export const useGetSuppliesFromFixedEstablishment =
  (): ISuppliesFromFixedEstablishmentOutput => {
    const data = useShallowEqualSelector((state: any) =>
      _.get(
        state[ReducerKeys.VATRETURN_DETAIL_REDUCER],
        'vatReturnDetail.suppliesFromFixedEstablishments'
      )
    );
    return data;
  };

export interface ICountry {
  country_id: number;
  country: string;
  vat_rate: Array<ICountryVatRate>;
}

interface ICountryVatRate {
  vat_rate_id: number;
  vat_rate_type_id: number;
  vat_rate: number;
  vat_rate_type: string;
}

interface IOutputCountriesWithVatRate {
  data: Array<ICountry>;
  status: StateStatus;
}

export const useGetCountriesWithVatRate = (): IOutputCountriesWithVatRate => {
  const data = useShallowEqualSelector((state: any) =>
    _.get(state[ReducerKeys.VATRETURN_DETAIL_REDUCER], 'countries', {})
  );
  return data;
};

export const useGetFEstCountriesWithVatRate =
  (): IOutputCountriesWithVatRate => {
    const data = useShallowEqualSelector((state: any) =>
      _.get(state[ReducerKeys.VATRETURN_DETAIL_REDUCER], 'countriesFEst', {})
    );
    return data;
  };

interface IFixedEstablishment {
  fixed_establish_id: number;
  vat_id_num: string;
  id_type_id: number;
  country_id: number;
  country: string;
  start_date: Date;
  cessation_date: Date;
}
interface IOutputFixedEstablishments {
  data: Array<IFixedEstablishment>;
  status: StateStatus;
}

export const useGetFixedEstablishmentsOfVatReturn =
  (): IOutputFixedEstablishments => {
    const data = useShallowEqualSelector((state: any) =>
      _.get(
        state[ReducerKeys.VATRETURN_DETAIL_REDUCER],
        'fixedEstablishments',
        {}
      )
    );
    return data;
  };

interface IQuartersOutput {
  status: StateStatus;
  data: Array<IQuarters>;
}

export const useGetQuarters = (): IQuartersOutput => {
  const data = useShallowEqualSelector((state: any) =>
    _.get(state[ReducerKeys.VATRETURN_DETAIL_REDUCER], 'quarters', {})
  );
  return data;
};

export const useGetMonthQuarterList = (): IQuartersOutput => {
  const data = useShallowEqualSelector((state: any) =>
    _.get(state[ReducerKeys.VATRETURN_DETAIL_REDUCER], 'monthQuarterList', {})
  );
  return data;
};

interface IVatCorrection {
  vat_correction_id: number;
  vat_return_id: number;
  period_id: number;
  period: string;
  country_id: number;
  country: string;
  amount: number;
  year: number;
}

export const useGetVatCorrections = (): Array<IVatCorrection> => {
  const data = useShallowEqualSelector((state: any) =>
    _.get(
      state[ReducerKeys.VATRETURN_DETAIL_REDUCER],
      'vatReturnDetail.vatCorrections'
    )
  );
  return data;
};

interface ICorrectionsCountry {
  country_id: number;
  country: string;
  due_balance: number;
}

interface ICorrectionsCountryOutput {
  data: Array<ICorrectionsCountry>;
  status: StateStatus;
}

export const useGetCorrectionsCountries = (): ICorrectionsCountryOutput => {
  const data = useShallowEqualSelector((state: any) =>
    _.get(
      state[ReducerKeys.VATRETURN_DETAIL_REDUCER],
      'correctionsCountries',
      {}
    )
  );
  return data;
};

export const useGetVatReturnTemporarySubmittionStatus = (): StateStatus => {
  const temporary = useShallowEqualSelector(
    (state: any) =>
      state[ReducerKeys.VATRETURN_DETAIL_REDUCER].vatReturnDetail
        .temporarySaveStatus
  );

  return temporary;
};

export const useGetVatReturnFinalSubmittionStatus = (): StateStatus => {
  const final = useShallowEqualSelector(
    (state: any) =>
      state[ReducerKeys.VATRETURN_DETAIL_REDUCER].vatReturnDetail
        .finalSubmitStatus
  );

  return final;
};

export const useGetVatReturnNilSubmittionStatus = (): StateStatus => {
  const nil = useShallowEqualSelector(
    (state: any) =>
      state[ReducerKeys.VATRETURN_DETAIL_REDUCER].vatReturnDetail
        .submitNilStatus
  );
  return nil;
};
